import { FC, useMemo } from 'react';
import { Badge, Box, Flex, Heading, Icon, Text, chakra } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { RxComponentPlaceholder } from 'react-icons/rx';
import { FaCheckCircle, FaCopy, FaEdit, FaStopCircle, FaTimesCircle, FaTrashAlt } from 'react-icons/fa';
import { parseJSON } from 'date-fns';
import { AccountDataObjectListDataObjectsResponseItem } from '@netiva/classifieds-api';

import { useDate, useMatomoTagManager } from '@/hooks';
import { routes } from '@/lib/routes';
import { CommonStepKeys } from '@/pages/Ad/constants';
import { AdCard, AdCardContent, AdCardImage, AdCardLink } from './AdCard';
import { ActionButton } from '../../ActionButton';
import { StatusBadge } from '../../StatusBadge';
import { AdListItemActionFunc } from '../../../types';
import { VerificationStatus } from '../../VerificationStatus';
import { TopListingStatus } from '../../TopListingStatus';
import { AdMessagesButton } from '../../AdMessagesButton';

type AdItem = AccountDataObjectListDataObjectsResponseItem;

export interface AdProps {
  item: AdItem;
  onActionExecuted: AdListItemActionFunc;
  tabStatus?: string;
  hasMemberContract?: boolean;
}

const StatusIcons = chakra(Flex, {
  baseStyle: {
    justifyContent: 'center',
    position: 'absolute',
    top: 1,
    width: 'full',
  },
});
const StatusIconsInner = chakra(Flex, {
  baseStyle: {
    alignItems: 'center',
    gap: 1,
    backgroundColor: 'white',
    borderRadius: 'default',
  },
});

export const AdGridItem: FC<AdProps> = ({ item, onActionExecuted, tabStatus, hasMemberContract }) => {
  const { formatRange } = useDate();
  const { t } = useTranslation();

  const { buildMatomoTrackingAttributes, trackMatomoEvent } = useMatomoTagManager();

  const publicationDates = useMemo(
    () => formatRange(item.publicationStart, item.publicationEnd),
    [formatRange, item.publicationEnd, item.publicationStart]
  );

  const hasFutureStart = useMemo(
    () => (item.publicationStart ? parseJSON(item.publicationStart) > new Date() : false),
    [item.publicationStart]
  );

  const handleEditClick = (e: React.MouseEvent) => {
    if (item.allowEdit) {
      return;
    }

    e.preventDefault();
    onActionExecuted(item, 'showUneditableModal');
    trackMatomoEvent({
      trackingCategory: 'Interaction_PI-MyListing',
      trackingAction: 'Edit_Listing',
      trackingName: `Edit_Listing_${tabStatus}`,
    });
  };

  const deleteTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Delete_Listing',
    trackingName: `Delete_Listing_${tabStatus}`,
  });

  const activateTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Activate_Listing',
  });

  const deactivateTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Deactivate_Listing',
  });

  const duplicateTrackingProps = buildMatomoTrackingAttributes({
    trackingCategory: 'Interaction_PI-MyListing',
    trackingAction: 'Duplicate_Listing',
    trackingName: `Duplicate_Listing_${tabStatus}`,
  });

  return (
    <AdCard key={item.id}>
      <AdCardLink to={item.allowEdit ? routes.ad(item.id, CommonStepKeys.edit) : '#'} onClick={handleEditClick}>
        <StatusBadge status={item.status} hasFutureStart={hasFutureStart} />
        <StatusIcons>
          <StatusIconsInner>
            <VerificationStatus
              addressStatus={item.addressStatus}
              dataObjectStatus={item.status}
              dataObjectType={item.type}
            />
            {hasMemberContract && <TopListingStatus isTopListed={item.isTopListed} />}
          </StatusIconsInner>
        </StatusIcons>
        {item.imageUrl ? (
          <AdCardImage src={item.imageUrl} alt={item.title} />
        ) : (
          <Flex height={150} flexShrink={0} marginBottom={2} justify="center" align="center">
            <Icon as={RxComponentPlaceholder} boxSize={12} />
          </Flex>
        )}
        <AdCardContent>
          <Heading as="h3" size="md" mb={4} wordBreak="break-all">
            {item.title}
          </Heading>
          <Badge colorScheme="secondary" fontSize="sm" mb={4} whiteSpace="normal">
            {item.category}
          </Badge>
          {publicationDates && <Text fontSize="sm">{publicationDates}</Text>}
        </AdCardContent>
        {item.allowEdit && (
          <Box
            position="absolute"
            top={3}
            right={3}
            overflow="hidden"
            p={4}
            border="default"
            borderRadius="full"
            background="neutral.50"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Icon as={FaEdit} />
          </Box>
        )}
      </AdCardLink>
      <Flex justify="center" align="center" gap={4} paddingBottom={2}>
        {item.allowDelete && (
          <ActionButton
            iconType={FaTrashAlt}
            label={t('myads.actions.delete')}
            onClick={() => onActionExecuted(item, 'delete')}
            hideLabel
            {...deleteTrackingProps}
          />
        )}
        {item.allowEnable && (
          <ActionButton
            iconType={FaCheckCircle}
            label={t('myads.actions.activate')}
            onClick={() => onActionExecuted(item, 'enable')}
            hideLabel
            {...activateTrackingProps}
          />
        )}
        {item.allowDisable && (
          <ActionButton
            iconType={FaTimesCircle}
            label={t('myads.actions.deactivate')}
            onClick={() => onActionExecuted(item, 'disable')}
            hideLabel
            {...deactivateTrackingProps}
          />
        )}
        {item.allowStop && (
          <ActionButton
            iconType={FaStopCircle}
            label={t('myads.actions.stop')}
            onClick={() => onActionExecuted(item, 'stop')}
            hideLabel
          />
        )}
        <ActionButton
          iconType={FaCopy}
          label={t('myads.actions.duplicate')}
          onClick={() => onActionExecuted(item, 'duplicate')}
          hideLabel
          {...duplicateTrackingProps}
        />
        <AdMessagesButton item={item} compact />
      </Flex>
    </AdCard>
  );
};
